import { Box, Slide } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { ButtonRow } from './controls/ButtonRow';
import { DropdownController } from './controls/DropdownController';
import { Seekbar } from './controls/Seekbar';
import { usePlayerContext } from './PlayerContext';
import { Dropdowns } from './types';

const defaultSeekRange = { start: 0, end: 0 };

type ControlsProps = {
  show: boolean;
  onToggleFullscreen(): void;
  container: HTMLDivElement | null;
};

export const Controls: FC<ControlsProps> = ({ show, onToggleFullscreen, container }) => {
  const { player } = usePlayerContext();
  const [activeDropdown, setActiveDropdown] = useState<Dropdowns>('hidden');

  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1); // range <0 - 1>
  const [muted, setMuted] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [lookback, setLookback] = useState(0);
  const [seekRange, setSeekRange] = useState(defaultSeekRange);

  useEffect(() => {
    const video = player?.getMediaElement();
    if (video) {
      const playHandler = () => {
        setIsPlaying(true);
        setDuration(video.duration ? video.duration : 0);
        setMuted(video.muted);
        if (video.muted) {
          video.volume = 0;
        }
        setVolume(video.volume);
      };
      const pauseHandler = () => {
        setIsPlaying(false);
      };
      const timeUpdateHandler = () => {
        if (video.buffered.length > 0) {
          const lookback = video.buffered.end(video.buffered.length - 1) - video.currentTime;
          setLookback(Math.floor(lookback));
        }
        setPosition(video.currentTime);
        setDuration(video.duration ? video.duration : 0);
        setSeekRange(player?.seekRange() ?? { start: 0, end: 0 });
      };
      const volumeChangeHandler = () => {
        setVolume(video.volume);
      };

      video.addEventListener('play', playHandler);
      video.addEventListener('pause', pauseHandler);
      video.addEventListener('timeupdate', timeUpdateHandler);
      video.addEventListener('volumechange', volumeChangeHandler);

      const clickHandler = () => {
        video.paused ? video.play() : video.pause();
      };

      const doubleClickHandler = () => {
        onToggleFullscreen();
      };

      video.addEventListener('click', clickHandler);
      video.addEventListener('dblclick', doubleClickHandler);

      return () => {
        video.removeEventListener('play', playHandler);
        video.removeEventListener('pause', pauseHandler);
        video.removeEventListener('timeupdate', timeUpdateHandler);
        video.removeEventListener('volumechange', volumeChangeHandler);

        video.removeEventListener('click', clickHandler);
        video.removeEventListener('dblclick', doubleClickHandler);
      };
    }
  }, [player, onToggleFullscreen]);

  const togglePlaying = useCallback(() => {
    const video = player?.getMediaElement();
    if (video) {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    }
  }, [player]);

  const volumeChange = useCallback(
    (value: number) => {
      const video = player?.getMediaElement();
      if (video) {
        if (value === 0) {
          setMuted(true);
          video.muted = true;
        } else {
          setMuted(false);
          video.muted = false;
        }

        video.volume = value;
      }
    },
    [player],
  );

  const toggleMute = useCallback(() => {
    const video = player?.getMediaElement();
    if (video) {
      const currentlyMuted = video.muted;
      video.muted = !currentlyMuted;
      volumeChange(currentlyMuted ? 1 : 0);
    }
  }, [player, volumeChange]);

  const jumpLive = useCallback(() => {
    const video = player?.getMediaElement();

    if (video) {
      video.currentTime = video.duration - 1;
    }
  }, [player]);

  const toggleDropdown = useCallback(
    (kind: Dropdowns) => {
      if (activeDropdown === kind) {
        setActiveDropdown('hidden');
      } else {
        setActiveDropdown(kind);
      }
    },
    [activeDropdown],
  );

  const hideDropdown = useCallback(() => {
    setActiveDropdown('hidden');
  }, []);

  const seekable = seekRange.end - seekRange.start > 1;

  return (
    <Slide in={show} appear={false} direction="up" timeout={200} container={container}>
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          maxHeight: '80%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          padding: '16px',
          zIndex: 2,
        }}
        onClick={hideDropdown}
      >
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(180deg, rgba(152, 161, 168, 0) 0%, rgba(152, 161, 168, 0.7) 100%)',
            height: '120px',
            pointerEvents: 'none',
          }}
        />
        <DropdownController active={activeDropdown} onHide={hideDropdown} />
        {seekable && (
          <Seekbar
            position={position}
            {...(player?.isLive() ? { min: seekRange.start, max: seekRange.end } : { max: duration })}
          />
        )}
        <ButtonRow
          muted={muted}
          isPlaying={isPlaying}
          position={position}
          duration={duration}
          lookback={lookback}
          seekable={seekable}
          volume={volume}
          activeDropdown={activeDropdown}
          onToggleMute={toggleMute}
          onTogglePlaying={togglePlaying}
          onChangeVolume={volumeChange}
          onToggleFullscreen={onToggleFullscreen}
          onToggleDropdown={toggleDropdown}
          onJumpLive={jumpLive}
        />
      </Box>
    </Slide>
  );
};
